import {
  GetSubmissionResponsePortal,
  SubmissionPortal,
} from "@rubi/api-generated";

import { formatDateShort } from "@/common/utils";
import { isPrototypeKey } from "@/components/online-reporting/utils";

export const isReportClosed = (
  reportMetadata: GetSubmissionResponsePortal | SubmissionPortal,
) => {
  return (
    reportMetadata.status === "approved" ||
    reportMetadata.status === "rejected" ||
    reportMetadata.status === "escalated"
  );
};

export const getReportTitle = (
  isSupplementaryReport: boolean,
  title: string,
): string => {
  if (!isSupplementaryReport && title === "Supplementary Report") {
    return "General Report (Submitted as Supplementary)";
  }
  return title;
};

/** Current values of the editable detail fields on a report. */
export interface DetailFieldValues {
  incidentNumber: string;
  notes: string;
  synopsis: string;
  /** Main-view only (string form, matching the priority input). Omit to skip. */
  priority?: string;
  /** Main-view only. Omit to skip. */
  tags?: string[];
}

/** Saved detail values to compare against (a subset of the report metadata). */
export interface SavedDetailFields {
  incident_number?: string | null;
  notes?: string | null;
  synopsis?: string | null;
  priority?: number | null;
  tags?: string | null;
}

/**
 * Whether a report's editable detail fields differ from what's saved. Shared by
 * the condensed main view (all fields, from uncontrolled refs) and the notes
 * panel (notes/synopsis/incident only, from controlled state) so both derive
 * the dirty state the same way. Fields left `undefined` in `current` are not
 * compared — e.g. the panel can't edit priority/tags.
 */
export const detailsDifferFromSaved = (
  current: DetailFieldValues,
  saved: SavedDetailFields,
): boolean => {
  if (current.incidentNumber !== (saved.incident_number ?? "")) return true;
  if (current.notes !== (saved.notes ?? "")) return true;
  if (current.synopsis !== (saved.synopsis ?? "")) return true;
  if (
    current.priority !== undefined &&
    current.priority !== (saved.priority?.toString() ?? "0")
  ) {
    return true;
  }
  if (current.tags !== undefined) {
    const savedTags = saved.tags?.split(",").map((tag) => tag.trim()) ?? [];
    if (current.tags.join(",") !== savedTags.join(",")) return true;
  }
  return false;
};

export const getReportTimeDisplay = (
  timeCreated: string,
  timeUpdated?: string,
) => {
  if (!timeUpdated) {
    return `Time Submitted: ${formatDateShort(timeCreated)}`;
  } else if (timeCreated === timeUpdated) {
    return `Time Submitted: ${formatDateShort(timeCreated)}`;
  } else {
    const diffInMinutes =
      Math.abs(
        new Date(timeCreated).getTime() - new Date(timeUpdated).getTime(),
      ) /
      (1000 * 60);
    if (diffInMinutes < 5) {
      return `Time Submitted: ${formatDateShort(timeCreated)}`;
    } else {
      return `Time Updated: ${formatDateShort(timeUpdated)}`;
    }
  }
};

/** Unsaved notes/synopsis on a supplemental, held by the report view. */
export interface SupplementalNotesDraft {
  notes: string;
  synopsis: string;
}

export type SupplementalNotesDrafts = Record<string, SupplementalNotesDraft>;

/** Applies edits to a supplemental's draft. A draft back at the saved values is dropped, so only real edits count as unsaved. */
export const withSupplementalDraft = (
  drafts: SupplementalNotesDrafts,
  submissionId: string,
  changes: Partial<SupplementalNotesDraft> | null,
  saved: SavedDetailFields,
): SupplementalNotesDrafts => {
  const { [submissionId]: current, ...rest } = drafts;
  if (!changes) return rest;
  const savedNotes = saved.notes ?? "";
  const savedSynopsis = saved.synopsis ?? "";
  const draft = {
    ...(current ?? { notes: savedNotes, synopsis: savedSynopsis }),
    ...changes,
  };
  if (draft.notes === savedNotes && draft.synopsis === savedSynopsis) {
    return rest;
  }
  return { ...rest, [submissionId]: draft };
};

const parsePath = (path: string): (string | number)[] => {
  const keys: (string | number)[] = [];
  let current = "";
  let inBrackets = false;

  for (let i = 0; i < path.length; i++) {
    const char = path[i];

    if (char === "[") {
      if (current) {
        keys.push(current);
        current = "";
      }
      inBrackets = true;
    } else if (char === "]") {
      if (inBrackets && current) {
        const index = parseInt(current, 10);
        if (!isNaN(index)) {
          keys.push(index);
        }
        current = "";
      }
      inBrackets = false;
    } else if (char === "." && !inBrackets) {
      if (current) {
        keys.push(current);
        current = "";
      }
    } else {
      current += char;
    }
  }

  if (current) {
    keys.push(current);
  }

  return keys;
};

export const setNestedValue = (obj: any, path: string, value: string): void => {
  const keys = parsePath(path);
  if (keys.length === 0 || keys.some(isPrototypeKey)) return;

  let current = obj;

  // Navigate to the parent of the target key
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];

    if (typeof key === "number") {
      // Array index
      if (!Array.isArray(current)) {
        return; // Invalid path - can't access array index on non-array
      }
      if (!current[key]) {
        current[key] = {}; // Initialize array element if it doesn't exist
      }
      current = current[key];
    } else {
      // Object property
      if (!current[key]) {
        current[key] = {}; // Initialize object property if it doesn't exist
      }
      current = current[key];
    }
  }

  // Set the final value
  const finalKey = keys[keys.length - 1];
  if (typeof finalKey === "number") {
    if (Array.isArray(current)) {
      current[finalKey] = value;
    }
  } else {
    current[finalKey] = value;
  }
};
